import { IPlatformServices } from '@wix/native-components-infra/dist/src/types/types';
import { SiteStore } from '@wix/wixstores-client-storefront-sdk';
import { EcomPlatformViewerScriptContext } from '../viewer-script/types';

export interface IRavenUserContext {
  id: string;
  url: string;
}

export type AnyFunction = (...args: any[]) => any;
export type AnyObject = Record<string, unknown>;
const noop = () => {};

function isFunction(value: unknown): value is AnyFunction {
  return typeof value === 'function';
}

function omit<T extends Record<string, unknown>, K extends keyof T>(
  obj: T,
  keyToOmit: K,
): Omit<T, K> {
  const { [keyToOmit]: _omitted, ...rest } = obj;
  return rest;
}

export interface IRavenInstance {
  captureException(ex: Error | ErrorEvent | string, options?: any): void;
}

let sentryReporterInstance: any;
let shouldWrapAsyncFunctionsWithPromise = false;

export function setSentryInstance(sentryInstance: any) {
  sentryReporterInstance = sentryInstance;
}

export function wrapAsyncFunctionsWithPromise(shouldWrap: any) {
  shouldWrapAsyncFunctionsWithPromise = shouldWrap;
}

export const wrapTryCatch = (onCatch: any) => (fn: any) => {
  return function () {
    try {
      return shouldWrapAsyncFunctionsWithPromise
        ? Promise.resolve(fn.apply(null, arguments)).catch(onCatch)
        : fn.apply(null, arguments);
    } catch (e) {
      /* istanbul ignore next */
      onCatch?.(e);
    }
  };
};

/* istanbul ignore next */
export const errorReporter = (e: any, rethrowException = true): any => {
  sentryReporterInstance?.captureException(e);

  if (rethrowException) {
    throw e;
  }
};

export function withErrorReportingWrapping<T extends AnyObject>(obj: T): T {
  return withErrorReporting(errorReporter)(obj);
}

export function withErrorReporting(
  errorReporterFn: AnyFunction,
): <T extends object>(obj: T) => T {
  return <T extends object>(obj: T): T => {
    const wrapper = wrapTryCatch(errorReporterFn);
    return Object.keys(obj).reduce((result, key) => {
      const typedKey = key as keyof T;
      const value = obj[typedKey];
      return {
        ...result,
        [typedKey]: isFunction(value) ? wrapper(value) : value,
      };
    }, {} as T);
  };
}

export function createFakeRaven() {
  return (_dsn: any) => noop;
}

export function createControllerRaven(
  platformServices: IPlatformServices,
  userContext: IRavenUserContext,
  siteStore: SiteStore,
): EcomPlatformViewerScriptContext['createMonitoring'] {
  return (dsn: string, ravenOptions) => {
    const raven = platformServices.monitoring.createMonitor(dsn, (data) => {
      data.environment = 'Worker';
      Object.assign(data, ravenOptions);
      return data;
    });

    raven.setUserContext(userContext);

    return (ex: Error | ErrorEvent | string, options?: any) => {
      raven.setExtraContext(ex);

      siteStore.fetchAdapter.getHistory().forEach((h: any) => {
        const response = omit(h.data.rawResponse, 'headers');
        raven.captureBreadcrumb({
          message: `Fetch ${h.url}`,
          category: 'http',
          type: 'http',
          data: {
            body: JSON.stringify(h.data.body)?.substr(0, 100),
            errors: h.data.rawResponse.errors,
            response: JSON.stringify(response)?.substr(0, 300),
            status: h.data.rawResponse.status,
            headers: h.data.headers,
          },
        });
      });
      raven.captureException(ex, options);
      raven.setExtraContext();
      console.error(ex);
      throw ex;
    };
  };
}
